import React, { useState } from "react";
import { Button, Snackbar, Stack } from "@mui/material";
import { GroupColumn } from "./GroupTable";
import { PRIMARY } from "./parts";

/**
 * Grid / copy / CSV export, replicated locally from MetricTable's approach
 * (its helpers are private to that component) so every grouping exports the
 * exact columns it renders.
 */
export const buildGrid = <T,>(
  columns: GroupColumn<T>[],
  rows: T[],
): string[][] => {
  const header = columns.map((c) => c.label);
  const body = rows.map((row) =>
    columns.map((c) =>
      c.csvValue ? c.csvValue(row) : String(c.sortValue(row)),
    ),
  );
  return [header, ...body];
};

export const gridToTsv = (grid: string[][]) =>
  grid.map((r) => r.join("\t")).join("\n");

const csvEscape = (s: string) =>
  /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;

export const gridToCsv = (grid: string[][]) =>
  grid.map((r) => r.map(csvEscape).join(",")).join("\n");

export const downloadCsv = (grid: string[][], filename: string) => {
  const blob = new Blob([gridToCsv(grid)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export interface ExportBarProps {
  /** Built lazily so a large grid is only assembled on click. */
  getGrid: () => string[][];
  filename: string;
  disabled?: boolean;
}

const ExportBar: React.FC<ExportBarProps> = ({
  getGrid,
  filename,
  disabled,
}) => {
  const [snack, setSnack] = useState<string | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(gridToTsv(getGrid()));
      setSnack("Table copied to clipboard");
    } catch {
      setSnack("Could not copy to clipboard");
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2 }}>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={copy}
          sx={{ color: PRIMARY, borderColor: PRIMARY }}
        >
          Copy table
        </Button>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={() => downloadCsv(getGrid(), filename)}
          sx={{
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#7a184c" },
          }}
        >
          Download CSV
        </Button>
      </Stack>
      <Snackbar
        open={snack !== null}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </>
  );
};

export default ExportBar;
